import { TrackingProvider } from '@act/react-components/tracking';
import { SurveyV2, UserDto } from '@act/shared/data-transfer-objects';
import { FlexContainer, FormBuilder } from '@act/storybook';
import { Divider } from '@mui/material';
import { Control, FormState } from 'react-hook-form';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import z from 'zod';
import SurveyResponseSummaryView from '../../surveys/SurveyResponseSummaryView';
import SurveyStepper from '../../surveys/SurveyStepper';
import SurveyTitleSection from '../../surveys/SurveyTitleSection';

export const notesSchema = z.object({
  notes: z.string().optional(),
});

export type NotesForm = z.infer<typeof notesSchema>;

interface AssignedSurveyContentProps {
  assignedSurvey: SurveyV2.AssignedSurveyDto;
  member: UserDto;
  isReviewMode?: boolean;
  submittingForm: boolean;
  onFormStateChange?: (isDirty: boolean) => void;
  control: Control<NotesForm>;
  formState: FormState<NotesForm>;
  handleAutoSave?: (autoSaveNote: string) => Promise<void>;
}

export const AssignedSurveyContent = ({
  assignedSurvey,
  member,
  isReviewMode = false,
  submittingForm,
  onFormStateChange,
  control,
  formState,
  handleAutoSave,
}: AssignedSurveyContentProps) => {
  const { survey } = assignedSurvey || {};

  const formConfig: FormConfig<NotesForm> = [
    {
      type: 'input',
      name: 'notes',
      label: 'Notes',
      input: 'richText',
      props: {
        autoSaveFn: handleAutoSave,
        showCharacterCount: false,
      },
    },
  ];

  return (
    <TrackingProvider contextName="assigned-survey-content">
      <SurveyTitleSection
        survey={survey}
        member={member}
        assignmentCreatedAt={assignedSurvey.createdAt}
      />
      {!isReviewMode && (
        <SurveyStepper
          assignedSurvey={assignedSurvey}
          submittingForm={submittingForm}
          onFormStateChange={onFormStateChange}
        />
      )}
      {isReviewMode && (
        <SurveyResponseSummaryView assignedSurvey={assignedSurvey} />
      )}
      <FlexContainer
        direction="column"
        height="auto"
        margin="24px -12px 8px"
        overflowAllVisible
        width="100%"
      >
        <Divider />
      </FlexContainer>
      <FormBuilder
        config={formConfig}
        control={control}
        formState={formState}
      />
    </TrackingProvider>
  );
};
